import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Percent, History, Calculator, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

const BASE_PREVIEW = 10.0; // informational $10 example — the pricing engine stays authoritative

const money = (n) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;

// Client-side checks mirror the server for UX only — the server re-validates everything.
function validate(d, h, limits) {
  if (isNaN(d) || isNaN(h)) return "Enter numeric values for both commissions.";
  for (const [v, l] of [[d, "Driver"], [h, "Host"]]) {
    if (v < 0) return `${l} commission cannot be negative.`;
    if (v > limits.max_pct) return `${l} commission cannot exceed ${limits.max_pct.toFixed(2)}%.`;
    if (!/^\d+(\.\d{1,2})?$/.test(String(v))) return `${l} commission allows at most 2 decimal places.`;
  }
  if (d + h > limits.combined_max_pct) return `Combined commission cannot exceed ${limits.combined_max_pct.toFixed(2)}%.`;
  return null;
}

export default function AdminCommission() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [driver, setDriver] = useState("");
  const [host, setHost] = useState("");
  const [scope, setScope] = useState("consumer"); // consumer | commercial (separately versioned)
  const [taxStatus, setTaxStatus] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.adminCommission();
      setData(d); setError(null);
      const cur = scope === "commercial" ? d.current_commercial : d.current;
      setDriver(String(cur.driver_pct));
      setHost(String(cur.host_pct));
      setConfirming(false);
    } catch (e) { setError(e.message); if (e.status !== 403) toast.error(e.message); }
  }, [scope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.adminTaxStatus().then(setTaxStatus).catch(() => {}); }, []);

  const d = parseFloat(driver), h = parseFloat(host);
  const limits = data?.limits || { max_pct: 25, combined_max_pct: 50 };
  const validationError = useMemo(() => validate(d, h, limits), [d, h, limits]);
  const scopedCurrent = data ? (scope === "commercial" ? data.current_commercial : data.current) : null;
  const dirty = scopedCurrent && (d !== scopedCurrent.driver_pct || h !== scopedCurrent.host_pct);

  const publish = async () => {
    setBusy(true);
    try {
      await api.adminPublishCommission({ driver_commission: d, host_commission: h, scope });
      toast.success(`${scope === "commercial" ? "Commercial" : "Consumer"} commission published — driver ${d}%, host ${h}%`);
      setConfirming(false);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (error && !data) return <p className="text-sm text-red-400" data-testid="commission-error">{error}</p>;
  if (!data) return <p className="text-sm text-zinc-500" data-testid="commission-loading">Loading commission configuration…</p>;

  const cur = scopedCurrent;
  return (
    <div className="space-y-6" data-testid="admin-commission-page">
      {/* Scope toggle: consumer and commercial configurations are versioned separately */}
      <div className="flex gap-1 p-1 rounded-xl border border-[#27272A] bg-[#0D1114]/80 w-fit" data-testid="commission-scope-toggle" role="tablist" aria-label="Commission scope">
        {[["consumer", "Consumer"], ["commercial", "Commercial"]].map(([v, l]) => (
          <button key={v} role="tab" aria-selected={scope === v} data-testid={`commission-scope-${v}`} onClick={() => setScope(v)}
                  className={`px-4 py-1.5 rounded-lg text-[0.7rem] font-bold tracking-[0.1em] uppercase transition-colors ${scope === v ? "bg-[#E50914] text-white" : "text-zinc-500 hover:text-zinc-200"}`}>{l}</button>
        ))}
      </div>
      {/* Current configuration */}
      <div className="card-dark p-7">
        <div className="flex items-center gap-2.5">
          <Percent className="w-4 h-4 text-[#FF3B45]" />
          <h2 className="font-display font-semibold text-lg">Commission &amp; Fees</h2>
        </div>
        <p className="mt-1.5 text-xs text-zinc-500">Server-authoritative rates applied to every new quote, booking, extension, subscription, wallet settlement and payout. Historical transactions keep the rates they were created with.</p>
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="commission-current">
          {[["Driver commission", cur.driver_pct], ["Host commission", cur.host_pct], ["Total MyRedPark commission", cur.total_pct]].map(([l, v], i) => (
            <div key={l} className="rounded-xl border border-[#27272A] bg-[#0D1114]/60 p-4">
              <p className="meta-label">{l}</p>
              <p className={`mt-2 font-display font-bold text-2xl ${i === 2 ? "text-[#FF3B45]" : "text-white"}`} data-testid={`commission-current-${["driver", "host", "total"][i]}`}>{v}%</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-zinc-600" data-testid="commission-current-version">Active version v{cur.version ?? "—"} · effective {cur.effective_at ? new Date(cur.effective_at).toLocaleString() : "env seed"}</p>
      </div>

      {/* Stripe Tax configuration — live diagnostic from the platform Stripe account */}
      {taxStatus && (
        <div className="card-dark p-7" data-testid="tax-config-card">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className={`w-4 h-4 ${taxStatus.blockers?.length ? "text-amber-400" : "text-emerald-400"}`} />
            <h2 className="font-display font-semibold text-lg">Tax configuration (Stripe Tax)</h2>
          </div>
          <p className="mt-1.5 text-xs text-zinc-500">Live state of the platform's Stripe Tax setup ({taxStatus.mode} mode). Checkout taxes are always computed by Stripe Tax — never hardcoded rates.</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rounded-lg bg-[#080B0D] border border-[#27272A]"><p className="text-zinc-500">Status</p><p className={taxStatus.settings_status === "active" ? "text-emerald-400" : "text-amber-400"} data-testid="tax-settings-status">{taxStatus.settings_status || "unknown"}</p></div>
            <div className="p-3 rounded-lg bg-[#080B0D] border border-[#27272A]"><p className="text-zinc-500">Active registrations</p><p className="text-zinc-200" data-testid="tax-registration-count">{taxStatus.registrations?.filter((r) => r.status === "active").length ?? 0}</p></div>
            <div className="p-3 rounded-lg bg-[#080B0D] border border-[#27272A]"><p className="text-zinc-500">Tax codes</p><p className="text-zinc-200">Parking {taxStatus.tax_codes?.parking} · Fee {taxStatus.tax_codes?.platform_fee}</p></div>
          </div>
          {taxStatus.registrations?.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-zinc-400" data-testid="tax-registration-list">
              {taxStatus.registrations.map((r) => <li key={r.id}>{r.province ? `${r.country}-${r.province}` : r.country} ({r.province ? "provincial" : "country-wide"} {r.reg_type === "province_standard" ? "PST/QST" : "GST/HST"}) — {r.status}{r.active_from ? ` · from ${new Date(r.active_from * 1000).toLocaleDateString()}` : ""}</li>)}
            </ul>
          )}
          {taxStatus.blockers?.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-1.5" data-testid="tax-config-blockers" role="alert">
              {taxStatus.blockers.map((b, i) => <p key={i} className="text-xs text-amber-300">{b}</p>)}
            </div>
          )}
        </div>
      )}

      {/* Edit + preview */}
      <div className="card-dark p-7">
        <div className="flex items-center gap-2.5">
          <Calculator className="w-4 h-4 text-[#FF3B45]" />
          <h3 className="font-display font-semibold text-base">Publish new rates</h3>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="commission-driver" className="label-dark">Driver Commission (%)</label>
            <input id="commission-driver" data-testid="commission-driver-input" type="number" min="0" max={limits.max_pct} step="0.01"
                   value={driver} onChange={(e) => { setDriver(e.target.value); setConfirming(false); }} className="input-dark" />
          </div>
          <div>
            <label htmlFor="commission-host" className="label-dark">Host Commission (%)</label>
            <input id="commission-host" data-testid="commission-host-input" type="number" min="0" max={limits.max_pct} step="0.01"
                   value={host} onChange={(e) => { setHost(e.target.value); setConfirming(false); }} className="input-dark" />
          </div>
        </div>
        {validationError && dirty ? <p className="mt-3 text-xs text-red-400" role="alert" data-testid="commission-validation-error">{validationError}</p> : null}

        {!validationError && (
          <div className="mt-5 rounded-xl border border-[#27272A] bg-[#0D1114]/60 p-5" data-testid="commission-preview">
            <p className="meta-label">Preview · base parking amount {money(BASE_PREVIEW)}</p>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-400"><span>Driver commission ({d}%)</span><span className="text-white font-semibold" data-testid="preview-driver-fee">{money(BASE_PREVIEW * d / 100)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>Host commission ({h}%)</span><span className="text-white font-semibold" data-testid="preview-host-fee">{money(BASE_PREVIEW * h / 100)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>Total MyRedPark commission</span><span className="text-[#FF3B45] font-semibold" data-testid="preview-total-fee">{money(BASE_PREVIEW * (d + h) / 100)}</span></div>
              <div className="flex justify-between text-zinc-400 border-t border-[#1F2328] pt-1.5"><span>Host net parking payout</span><span className="text-emerald-400 font-semibold" data-testid="preview-host-payout">{money(BASE_PREVIEW * (1 - h / 100))}</span></div>
            </div>
            <p className="mt-3 text-[11px] text-zinc-600">Processing fees and taxes continue to be calculated by the existing systems. Informational only — production pricing always runs server-side.</p>
          </div>
        )}

        {!confirming ? (
          <button data-testid="commission-publish-button" disabled={!dirty || !!validationError} onClick={() => setConfirming(true)}
                  className="btn-primary mt-5 text-sm disabled:opacity-40">Save / Publish Changes</button>
        ) : (
          <div className="mt-5 rounded-xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="commission-confirm-dialog" role="alertdialog" aria-label="Confirm commission change">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <p className="text-sm font-semibold">Confirm financial configuration change</p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-[#27272A] p-3">
                <p className="meta-label">Current</p>
                <p className="mt-1 text-zinc-300">Driver: {cur.driver_pct}% · Host: {cur.host_pct}%</p>
              </div>
              <div className="rounded-lg border border-[#FF3B45]/40 p-3">
                <p className="meta-label">New</p>
                <p className="mt-1 text-white font-semibold" data-testid="confirm-new-rates">Driver: {d}% · Host: {h}%</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-amber-300/90" data-testid="confirm-total-change">
              Total commission changes from {cur.total_pct}% to {Math.round((d + h) * 100) / 100}%. Applies to future transactions only — existing bookings, invoices, refunds and payouts keep their original snapshots.
            </p>
            <div className="mt-4 flex gap-2">
              <button data-testid="commission-confirm-button" disabled={busy} onClick={publish} className="btn-primary !py-2 text-xs disabled:opacity-40">{busy ? "Publishing…" : "Confirm & Publish"}</button>
              <button data-testid="commission-cancel-button" disabled={busy} onClick={() => setConfirming(false)} className="btn-secondary !py-2 text-xs">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="card-dark p-7">
        <div className="flex items-center gap-2.5">
          <History className="w-4 h-4 text-[#FF3B45]" />
          <h3 className="font-display font-semibold text-base">Configuration history</h3>
        </div>
        <p className="mt-1.5 text-xs text-zinc-500">Append-only versions. Corrections are published as a new version — history is never edited.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="commission-history-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-600 border-b border-[#1F2328]">
                <th className="py-2 pr-4">Version</th><th className="py-2 pr-4">Scope</th><th className="py-2 pr-4">Driver</th><th className="py-2 pr-4">Host</th><th className="py-2 pr-4">Total</th><th className="py-2 pr-4">Effective</th><th className="py-2 pr-4">Changed by</th><th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((c) => (
                <tr key={c.config_id || "seed"} className="border-b border-[#1F2328]/60" data-testid={`commission-history-v${c.scope || "consumer"}-${c.version}`}>
                  <td className="py-2.5 pr-4 font-display font-semibold text-white">v{c.version}</td>
                  <td className="py-2.5 pr-4 text-zinc-400 text-xs uppercase">{c.scope || "consumer"}</td>
                  <td className="py-2.5 pr-4 text-zinc-300">{c.driver_pct}%</td>
                  <td className="py-2.5 pr-4 text-zinc-300">{c.host_pct}%</td>
                  <td className="py-2.5 pr-4 text-zinc-300">{c.total_pct}%</td>
                  <td className="py-2.5 pr-4 text-zinc-500 text-xs">{c.effective_at ? new Date(c.effective_at).toLocaleString() : "—"}</td>
                  <td className="py-2.5 pr-4 text-zinc-500 text-xs">{c.changed_by === "system:env-seed" ? "System (initial)" : (c.changed_by || "—")}</td>
                  <td className="py-2.5"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/40 text-zinc-500"}`} data-testid={`commission-status-v${c.version}`}>{c.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
